import { useMemo } from "react";
import { useCurrentProject } from "./useCurrentProject";
import { PERMISSION, type PermissionKey } from "@/lib/permissions";

/**
 * Granular permission flags derived from the current user's role on the
 * current project. Superadmins get every flag set to true.
 */
export interface Permissions {
  // Dashboard
  canViewDashboard: boolean;

  // Companies
  canViewAll: boolean;
  canViewScore: boolean;
  canCreate: boolean;
  canEdit: boolean;
  canDelete: boolean;
  canArchive: boolean;
  canImport: boolean;
  /** Export the (filtered) companies list to CSV. Gates the
   *  Companies-page Export CSV button. */
  canExportCompanies: boolean;
  canRescore: boolean;
  canRescoreAll: boolean;
  /** Reorder sections (Information / Funding / Signals / …) on
   *  the Company-detail page via the layout-edit mode. */
  canCustomizeLayout: boolean;
  /** Show/hide + reorder individual fields inside each Company-
   *  detail section via the per-card ⚙ customiser popover. */
  canCustomizeFields: boolean;
  /** Apply a bulk status / owner / tag mutation across the
   *  current selection. Gates the BulkActionsBar's change-status
   *  and assign-owner buttons. */
  canBulkUpdateCompanies: boolean;
  /** Permanently delete companies in bulk. No UI surface yet —
   *  kept as a flag so a future Delete Selected button can plug
   *  in without another perms round-trip. */
  canBulkDeleteCompanies: boolean;

  // Contacts
  canViewContacts: boolean;
  canCreateContacts: boolean;
  canEditContacts: boolean;
  canDeleteContacts: boolean;
  /** Export the (filtered) contacts list to CSV. Gates the
   *  Contacts-page Export CSV button. */
  canExportContacts: boolean;

  // Activity
  canViewActivity: boolean;
  canLogCall: boolean;
  canLogNote: boolean;
  canStartCallTimer: boolean;
  canEditNotes: boolean;
  /** Open the Call Center page. Required for the operator queue
   *  + the session-log inline form. */
  canAccessCallCenter: boolean;
  /** Show the Call Center Stats tab. */
  canViewCallCenterStats: boolean;
  /** See the whole team's Stats / History — unlocks the operator
   *  picker on those tabs. Without it a user only sees their own
   *  calls. */
  canViewCallCenterTeam: boolean;

  // Analytics
  canViewAnalytics: boolean;
  canExportAnalytics: boolean;

  // Calendar
  canViewCalendar: boolean;
  canCreateCalendarEvent: boolean;

  // Engine
  canViewEngine: boolean;
  canTriggerEngine: boolean;
  canPauseResumeEngine: boolean;
  canConfigureEngine: boolean;

  // Settings
  canViewSettings: boolean;
  canManageMembers: boolean;
  canEditProject: boolean;
  canEditScoring: boolean;
  canManageRoles: boolean;

  // Tags
  canViewTags: boolean;
  canManageTags: boolean;
  canAssignTags: boolean;

  // Email module
  /** Open the Email page: outreach.view AND contacts.view (it lists
   *  people), as the backend checks it. */
  canViewEmail: boolean;
  /** Create campaigns and edit their templates and AI variables. */
  canManageEmailCampaigns: boolean;

  // Enrichment workstation
  /** Open the Enrichment page (the operator queue + stats tab). */
  canAccessEnrichment: boolean;
  /** Submit enrichments — earns from the per-field rate card.
   *  Drives the audience of the Settings > Enrichment Rates list. */
  canWorkEnrichment: boolean;
  /** Show the Enrichment Stats & Earnings tab. */
  canViewEnrichmentStats: boolean;
  /** Edit the project's enrichment rate cards (defaults + per-user
   *  overrides). Gates the Settings tab. */
  canManageEnrichmentRates: boolean;

  // Tasks
  canViewTasks: boolean;
  canCreateTasks: boolean;
  canEditTasks: boolean;
  canDeleteTasks: boolean;

  // Saved views
  canCreateViews: boolean;
  canShareViews: boolean;
}

const ALL_TRUE: Permissions = {
  canViewDashboard: true,
  canViewAll: true,
  canViewScore: true,
  canCreate: true,
  canEdit: true,
  canDelete: true,
  canArchive: true,
  canImport: true,
  canExportCompanies: true,
  canRescore: true,
  canRescoreAll: true,
  canCustomizeLayout: true,
  canCustomizeFields: true,
  canBulkUpdateCompanies: true,
  canBulkDeleteCompanies: true,
  canViewContacts: true,
  canCreateContacts: true,
  canEditContacts: true,
  canDeleteContacts: true,
  canExportContacts: true,
  canViewActivity: true,
  canLogCall: true,
  canLogNote: true,
  canStartCallTimer: true,
  canEditNotes: true,
  canAccessCallCenter: true,
  canViewCallCenterStats: true,
  canViewCallCenterTeam: true,
  canViewAnalytics: true,
  canExportAnalytics: true,
  canViewCalendar: true,
  canCreateCalendarEvent: true,
  canViewEngine: true,
  canTriggerEngine: true,
  canPauseResumeEngine: true,
  canConfigureEngine: true,
  canViewSettings: true,
  canManageMembers: true,
  canEditProject: true,
  canEditScoring: true,
  canManageRoles: true,
  canViewTags: true,
  canManageTags: true,
  canAssignTags: true,
  canViewEmail: true,
  canManageEmailCampaigns: true,
  canAccessEnrichment: true,
  canWorkEnrichment: true,
  canViewEnrichmentStats: true,
  canManageEnrichmentRates: true,
  canViewTasks: true,
  canCreateTasks: true,
  canEditTasks: true,
  canDeleteTasks: true,
  canCreateViews: true,
  canShareViews: true,
};

const ALL_FALSE: Permissions = Object.fromEntries(
  Object.keys(ALL_TRUE).map((k) => [k, false]),
) as unknown as Permissions;

function fromKeys(keys: ReadonlySet<string>): Permissions {
  const has = (p: PermissionKey) => keys.has(p);
  return {
    canViewDashboard: has(PERMISSION.DASHBOARD_VIEW),

    canViewAll: has(PERMISSION.COMPANIES_VIEW_ALL),
    canViewScore: has(PERMISSION.COMPANIES_VIEW_SCORE),
    canCreate: has(PERMISSION.COMPANIES_CREATE),
    canEdit: has(PERMISSION.COMPANIES_EDIT),
    canDelete: has(PERMISSION.COMPANIES_DELETE),
    canArchive: has(PERMISSION.COMPANIES_ARCHIVE),
    canImport: has(PERMISSION.COMPANIES_IMPORT),
    // CSV export is a dedicated capability (the button used to ride on
    // companies.view_all, which let any viewer bulk-exfiltrate the list).
    // Gate strictly on the new companies.export key, falling back to
    // analytics.export — the pre-existing "export" capability — so roles
    // already trusted to export keep access until the backend catalog
    // ships the dedicated key.
    canExportCompanies:
      has(PERMISSION.COMPANIES_EXPORT) || has(PERMISSION.ANALYTICS_EXPORT),
    canRescore: has(PERMISSION.COMPANIES_RESCORE),
    canRescoreAll: has(PERMISSION.COMPANIES_RESCORE_ALL),
    // Layout reordering is admin-territory; field show/hide is
    // softer (members can usually do it). Both fall back to the
    // legacy settings.project.edit gate so existing admin roles
    // keep access while the backend catalog migrates.
    canCustomizeLayout:
      has(PERMISSION.COMPANIES_CUSTOMIZE_LAYOUT) ||
      has(PERMISSION.SETTINGS_PROJECT_EDIT),
    canCustomizeFields:
      has(PERMISSION.COMPANIES_CUSTOMIZE_FIELDS) ||
      has(PERMISSION.SETTINGS_PROJECT_EDIT),
    // Bulk gates fall back to the broader edit / delete keys so
    // existing roles don't lose the actions before the backend
    // catalog ships the new bulk_* keys.
    canBulkUpdateCompanies:
      has(PERMISSION.COMPANIES_BULK_UPDATE) || has(PERMISSION.COMPANIES_EDIT),
    canBulkDeleteCompanies:
      has(PERMISSION.COMPANIES_BULK_DELETE) || has(PERMISSION.COMPANIES_DELETE),

    canViewContacts: has(PERMISSION.CONTACTS_VIEW),
    canCreateContacts: has(PERMISSION.CONTACTS_CREATE),
    canEditContacts: has(PERMISSION.CONTACTS_EDIT),
    canDeleteContacts: has(PERMISSION.CONTACTS_DELETE),
    // Kept deliberately, unlike the two fallbacks above. `contacts.export`
    // is not a rename the front missed: it is a key the front added ahead
    // of the backend, and the catalog migration only ever landed
    // `companies.export`. Dropping the fallback today would take contact
    // export away from Admin (which holds `analytics.export`, not
    // `contacts.export`). Remove it once the backend ships the key.
    canExportContacts:
      has(PERMISSION.CONTACTS_EXPORT) || has(PERMISSION.ANALYTICS_EXPORT),

    canViewActivity: has(PERMISSION.ACTIVITY_VIEW),
    // `activity.log_call` was dropped when the backend renamed it to
    // `calls.log`: it is in no catalog and on no role, so the `||` only
    // hid the drift. `activity.log_note` is NOT the same case — it and
    // `notes.edit` both still exist server-side and are both granted,
    // so that one is a real either-or, not a legacy fallback.
    canLogCall: has(PERMISSION.CALLS_LOG),
    canLogNote: has(PERMISSION.NOTES_EDIT) || has(PERMISSION.ACTIVITY_LOG_NOTE),
    canStartCallTimer: has(PERMISSION.CALLS_START_TIMER),
    canEditNotes: has(PERMISSION.NOTES_EDIT) || has(PERMISSION.ACTIVITY_LOG_NOTE),
    // call_center.* are the new dedicated keys. Fall back to the
    // legacy calls.start_timer gate so existing operator roles
    // keep workspace access until the backend ships the catalog
    // update. view_stats also defaults to anyone who can access
    // the page — losing the tab would be a regression for current
    // users.
    canAccessCallCenter:
      has(PERMISSION.CALL_CENTER_ACCESS) || has(PERMISSION.CALLS_START_TIMER),
    canViewCallCenterStats:
      has(PERMISSION.CALL_CENTER_VIEW_STATS) ||
      has(PERMISSION.CALL_CENTER_ACCESS) ||
      has(PERMISSION.CALLS_START_TIMER),
    // Strictly the dedicated key — no fallback. This unlocks viewing
    // *other* operators' data, so it must never be granted implicitly
    // to roles that only have call_center.access.
    canViewCallCenterTeam: has(PERMISSION.CALL_CENTER_VIEW_TEAM),

    // Strictly mirrors the backend gate (analytics.view). The earlier
    // dashboard.view fallback was a backward-compat convenience that
    // ended up granting Analytics to everyone — the sidebar entry
    // showed for any user with dashboard.view while the backend still
    // returned 403 on the data endpoints. Drop the fallback so
    // frontend visibility matches what the API will actually serve.
    canViewAnalytics: has(PERMISSION.ANALYTICS_VIEW),
    canExportAnalytics: has(PERMISSION.ANALYTICS_EXPORT),

    canViewCalendar: has(PERMISSION.CALENDAR_VIEW) || has(PERMISSION.TASKS_VIEW),
    canCreateCalendarEvent: has(PERMISSION.CALENDAR_CREATE_EVENT) || has(PERMISSION.TASKS_CREATE),

    canViewEngine: has(PERMISSION.ENGINE_VIEW),
    canTriggerEngine: has(PERMISSION.ENGINE_TRIGGER),
    canPauseResumeEngine: has(PERMISSION.ENGINE_PAUSE_RESUME),
    canConfigureEngine: has(PERMISSION.ENGINE_CONFIGURE),

    canViewSettings: has(PERMISSION.SETTINGS_VIEW),
    canManageMembers: has(PERMISSION.SETTINGS_MEMBERS_MANAGE),
    canEditProject: has(PERMISSION.SETTINGS_PROJECT_EDIT),
    canEditScoring: has(PERMISSION.SETTINGS_SCORING_EDIT),
    canManageRoles: has(PERMISSION.SETTINGS_ROLES_MANAGE),

    canViewTags: has(PERMISSION.TAGS_VIEW),
    canManageTags: has(PERMISSION.TAGS_MANAGE),
    canAssignTags: has(PERMISSION.TAGS_ASSIGN),

    canViewEmail: has(PERMISSION.OUTREACH_VIEW) && has(PERMISSION.CONTACTS_VIEW),
    canManageEmailCampaigns: has(PERMISSION.OUTREACH_MANAGE_CAMPAIGNS),

    canAccessEnrichment: has(PERMISSION.ENRICHMENT_ACCESS),
    canWorkEnrichment: has(PERMISSION.ENRICHMENT_WORK),
    // Stats tab falls back to access — anyone who can open the
    // page keeps the tab until the backend ships the granular key.
    canViewEnrichmentStats:
      has(PERMISSION.ENRICHMENT_VIEW_STATS) ||
      has(PERMISSION.ENRICHMENT_ACCESS),
    canManageEnrichmentRates: has(PERMISSION.ENRICHMENT_MANAGE_RATES),

    canViewTasks: has(PERMISSION.TASKS_VIEW),
    canCreateTasks: has(PERMISSION.TASKS_CREATE),
    canEditTasks: has(PERMISSION.TASKS_EDIT),
    canDeleteTasks: has(PERMISSION.TASKS_DELETE),

    canCreateViews: has(PERMISSION.VIEWS_SAVE),
    canShareViews: has(PERMISSION.VIEWS_SHARE),
  };
}

export function usePermissions(): Permissions {
  const { role, isSuperadmin } = useCurrentProject();
  return useMemo(() => {
    if (isSuperadmin) return ALL_TRUE;
    if (!role) return ALL_FALSE;
    return fromKeys(new Set(role.permissions));
  }, [role, isSuperadmin]);
}
